<template>
  <div class="highlights-select-modal">
    <!-- Header -->
    <div class="header highlights-select-modal__header">
      <div class="header__back-link-wrap">
        <LinkWithIcon class="header__back-link" @click="emit('cancel')" isDottedUnderline>
          <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
          <span>Back</span>
        </LinkWithIcon>
      </div>

      <h2 class="global-h2 header__title">{{ displayedAlbumTitle }}</h2>
      <div v-if="props.subTitle" class="header__subtitle">
        {{ props.subTitle }}
      </div>
    </div>
    <!-- / Header -->

    <!-- Limit warning -->
    <div v-if="isAtLimit" class="limit-warning highlights-select-modal__limit-warning">Limit reached. Social Platform allows posting up to 10 images.</div>
    <!-- / Limit warning -->

    <!-- Album switcher -->
    <div v-if="!pickingFromContentSummaryId" class="album-switcher highlights-select-modal__album-switcher">
      <LinkWithIcon isDottedUnderline @click="isPickContentModalVisible = true">
        <template #icon><IconEmbedded name="folder_2" :size="19" /></template>
        <span>Select from another album</span>
      </LinkWithIcon>
      <span class="album-switcher__separator"> </span>
      <LinkWithIcon isDottedUnderline @click="isUploadSectionOpen = true">
        <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
        <span>Upload photos</span>
      </LinkWithIcon>
    </div>

    <div v-else class="album-switcher highlights-select-modal__album-switcher">
      <LinkWithIcon isDottedUnderline @click="clearExternalAlbum()">
        <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
        <span>Show original album</span>
      </LinkWithIcon>
    </div>
    <!-- / Album switcher -->

    <!-- Upload section -->
    <div v-if="isUploadSectionOpen" class="upload-section highlights-select-modal__upload-section">
      <div class="upload-section__header">
        <span class="upload-section__limit-text">{{ MAX_PHOTOS_PER_POST }} photos max</span>
        <LinkWithIcon isDottedUnderline @click="isUploadSectionOpen = false">
          <template #icon><IconEmbedded name="remove_2" :size="13" /></template>
          <span>Close</span>
        </LinkWithIcon>
      </div>
      
      <!-- Hidden upload form (provides the file input) -->
      <div class="upload-section__hidden-form">
        <UploadPhotoForm
          callerName="socialPostPhotoSelection"
          flow="social"
          :img="[]"
          :isVideoUploadAllowed="false"
          :maxNumberOfFilesToUploadAtOnce="MAX_PHOTOS_PER_POST"
          :isThumbnailsVisible="false"
          hideVideoGuidance
          inputId="social-post-upload"
          @uploadingStarted="isUploadingInProgress = true"
          @imageUploadedToServer="handleImageUploaded"
          @uploadProgressChange="({ isUploadInProgress }) => { if (!isUploadInProgress) isUploadingInProgress = false; }"
        />
      </div>
      
      <!-- Visible upload button + thumbnails row -->
      <ul class="upload-section__thumbnails-list">
        <!-- Upload button -->
        <li v-if="uploadedAssetIds.length < MAX_PHOTOS_PER_POST" class="add-files-button upload-section__thumbnail">
          <label class="add-files-button__label" for="social-post-upload">
            <template v-if="isUploadingInProgress">
              <LoadingIcon class="add-files-button__icon" :size="32" color="rgba(0, 0, 0, 0.5)" />
              <div class="add-files-button__text">Uploading<EllipsisAnimated /></div>
            </template>
            <template v-else>
              <IconEmbedded class="add-files-button__icon" name="upload_2" :size="32" color="rgba(0, 0, 0, 0.5)" />
              <div class="add-files-button__text">Upload</div>
            </template>
          </label>
        </li>
        
        <!-- Uploaded photo thumbnails inline -->
        <li v-for="assetId in uploadedAssetIds" :key="assetId" class="upload-section__thumbnail">
          <SrpFileThumbnail
            :fileName="assetId"
            galleryPostfix="uploadedPhotos"
            :isDeleteButton="false"
            isEntireAreaCheckable
          >
            <input
              type="checkbox"
              :checked="_selectedAssetIds.includes(assetId)"
              :disabled="isAtLimit && !_selectedAssetIds.includes(assetId)"
              @change="$event => selectOrDeselectFile(($event.target as HTMLInputElement).checked, assetId)"
            />
          </SrpFileThumbnail>
        </li>
      </ul>
    </div>
    <!-- / Upload section -->

    <!-- AI suggested alternatives
    <div v-if="props.fallbackAssetIds?.length" class="ai-suggested-alternatives highlights-select-modal__ai-suggested-alternatives">
      <div class="section-header ai-suggested-alternatives__section-header">
        <IconEmbedded class="section-header__icon" name="ai-triple-solid" :size="26" color="rgba(17, 134, 137, 1)" />

        <h4 class="global-h4 section-header__title">
          AI Suggested Alternatives
        </h4>
      </div>

      <ul class="ai-suggested-alternatives__thumbnails-list">
        <SrpFileThumbnail
          class="highlights-select-modal__thumbnail"
          v-for="assetId in props.fallbackAssetIds"
          :key="assetId"
          :fileName="assetId"
          galleryPostfix="postPhotoSelectionModal"
          :isDeleteButton="false"
          @delete="() => {}"
          isEntireAreaCheckable
        >
          <input
            type="checkbox"
            :value="assetId"
            :checked="_selectedAssetIds.includes(assetId)"
            :disabled="isAtLimit && !_selectedAssetIds.includes(assetId)"
            @change="$event => selectOrDeselectFile(($event.target as HTMLInputElement).checked, assetId)"
          />
        </SrpFileThumbnail>
      </ul>
    </div>
    AI suggested alternatives -->

    <ul v-if="isLoadingExternalAlbum" class="highlights-select-modal__thumbnails-list highlights-select-modal__thumbnails-list--skeleton">
      <li v-for="i in 12" :key="i" class="highlights-select-modal__thumbnail highlights-select-modal__thumbnail--skeleton"></li>
    </ul>

    <ul v-else class="highlights-select-modal__thumbnails-list">
      <SrpFileThumbnail
        class="highlights-select-modal__thumbnail"
        v-for="assetId in displayedAssetIds"
        :key="assetId"
        :fileName="assetId"
        galleryPostfix="postPhotoSelectionModal"
        :isDeleteButton="false"
        @delete="() => {}"
        isEntireAreaCheckable
      >
        <input
          type="checkbox"
          :value="assetId"
          :checked="_selectedAssetIds.includes(assetId)"
          :disabled="isAtLimit && !_selectedAssetIds.includes(assetId)"
          @change="$event => selectOrDeselectFile(($event.target as HTMLInputElement).checked, assetId)"
        />
      </SrpFileThumbnail>
    </ul>

    <SelectedFilesPanel
      v-if="_selectedAssetIds.length"
      class="highlights-select-modal__selected-files-panel"
      :selectedFiles="_selectedAssetIds.map(id => ({ serverId: id }))"
      @remove="assetId => selectOrDeselectFile(false, assetId)"
      @reorder="newArray => (_selectedAssetIds = newArray.map(({ serverId }) => serverId))"
      :isPreviewLinkVisible="false"
    />

    <PickFromExistingContentModal
      v-if="isPickContentModalVisible"
      v-model:isVisible="isPickContentModalVisible"
      :customerId="props.customerId"
      :calendarItem="null"
      @close="isPickContentModalVisible = false"
      @picked="handleContentSummarySelected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";

// Components
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import UploadPhotoForm from "@views/CMS/UploadPhotoForm.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";

// Repos
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Types
import { SocialShareSummaryPayload } from "@contracts/shareStop";
import { ContentSummaryPreview } from "@contracts/contentCalendar";
import { UploadedImage } from "@contracts/uploadedImage";

// Constants
import { MAX_PHOTOS_PER_POST } from "@/constants/social";

const props = withDefaults(
  defineProps<{
    customerId: string;
    title?: string;
    subTitle?: string;
    allAssetIds: Array<string>;
    // fallbackAssetIds: Array<string>;
    selectedAssetIds: Array<string>;
    saveCallBack?: ((selectedAssetIds: Array<string>) => Promise<void>) | null;
  }>(),
  {
    customerId: "",
    title: "Select Photos",
    subTitle: "",
    allAssetIds: () => [],
    // fallbackAssetIds: () => [],
    selectedAssetIds: () => [],
    saveCallBack: null,
  }
);

const emit = defineEmits<{
  (e: "save", selectedAssetIds: Array<string>): void;
  (e: "cancel"): void;
}>();

// External album state =======================================================
const isPickContentModalVisible = ref<boolean>(false);
const pickingFromContentSummaryId = ref<string | null>(null);
const externalAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const isLoadingExternalAlbum = ref<boolean>(false);

// Upload section state =======================================================
const isUploadSectionOpen = ref<boolean>(false);
const uploadedAssetIds = ref<Array<string>>([]);
const isUploadingInProgress = ref<boolean>(false);

// Selected assets not in content album =======================================
const selectedAssetsNotInContentAlbum = ref<Array<string>>([]);

// Select file ================================================================
function selectOrDeselectFile(isSelected: boolean, assetId: string) {
  if (isSelected) {
    _selectedAssetIds.value.push(assetId);
  } else {
    _selectedAssetIds.value = _selectedAssetIds.value.filter(_assetId => _assetId !== assetId);
  }
}

// Handle image uploaded ======================================================
function handleImageUploaded(uploadedImage: UploadedImage) {
  uploadedAssetIds.value.push(uploadedImage.serverId);
  // NOTE: NOT auto-selecting the newly uploaded photo
}

// selectedAssetIds local copy ================================================
const _selectedAssetIds = ref<Array<string>>([]);

onMounted(() => {
  _selectedAssetIds.value = props.selectedAssetIds;
  
  // Calculate selected assets that are not in the content album
  // This allows the user to deselect them without them completely disappearing.
  selectedAssetsNotInContentAlbum.value = props.selectedAssetIds.filter(
    assetId => !props.allAssetIds.includes(assetId)
  );
});

// Displayed asset IDs ========================================================
const displayedAssetIds = computed(() => {
  if (pickingFromContentSummaryId.value && externalAlbumPayload.value) {
    return externalAlbumPayload.value.assets;
  }
  // Append selected assets that are not in the content album
  return [...props.allAssetIds, ...selectedAssetsNotInContentAlbum.value];
});

const displayedAlbumTitle = computed(() => {
  if (pickingFromContentSummaryId.value && externalAlbumPayload.value) {
    return `Select from: "${externalAlbumPayload.value.title}"`;
  }
  return props.title;
});

// Selection limit ============================================================
const isAtLimit = computed(() => _selectedAssetIds.value.length >= MAX_PHOTOS_PER_POST);

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  if (props.saveCallBack) {
    isSaving.value = true;
    await props.saveCallBack(_selectedAssetIds.value);
    isSaving.value = false;
  }
  emit("save", _selectedAssetIds.value);
}

// Handle content summary selection ===========================================
async function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  isPickContentModalVisible.value = false;
  pickingFromContentSummaryId.value = contentSummary.id;
  
  isLoadingExternalAlbum.value = true;
  externalAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(
    props.customerId,
    contentSummary.id
  );
  isLoadingExternalAlbum.value = false;
}

function clearExternalAlbum() {
  pickingFromContentSummaryId.value = null;
  externalAlbumPayload.value = null;
}

// Define expose ==============================================================
defineExpose({
  save,
  cancel: () => emit("cancel"),
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/loading-bg.scss";

// Header =====================================================================
.header {
  &__back-link-wrap {
    padding-bottom: 15px;
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__back-link {
  }

  &__title {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__subtitle {
  }
}

// Limit warning ==============================================================
.limit-warning {
  padding: 12px 16px;
  margin-bottom: 20px;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.6);
  border-radius: 6px;
  color: #856404;
  font-weight: 600;
  text-align: center;
}

// Section header =============================================================
.section-header {
  display: flex;
  align-items: center;

  &__icon {
    margin-right: 2px;
  }

  &__title {
  }

  &__gray-text {
    color: rgba(0, 0, 0, 0.5);
    font-weight: 500;
  }
}

// AI suggested alternatives ==================================================
.ai-suggested-alternatives {
  padding: 23px 0 18px;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 60px);
    height: 100%;
    position: absolute;
    inset: 0 auto auto -30px;
    z-index: -1;
    background: rgba(229, 243, 242, 1);
  }

  &__section-header {
    margin-bottom: 13px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Album switcher =============================================================
.album-switcher {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &__separator {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
  }
  
  &__title {
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
  }
}

// Upload section =============================================================
.upload-section {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  background: rgba(245, 245, 245, 1);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  &__limit-text {
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    font-weight: 500;
  }

  &__hidden-form {
    width: 0;
    height: 0;
    overflow: hidden;
    opacity: 0;
    position: absolute;
  }

  &__thumbnails-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__thumbnail {
    width: 113px;
  }
}

// Add files button (compact square) ==========================================
.add-files-button {
  min-height: 85px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px rgba(0, 0, 0, 0.25) dashed;
  border-radius: 4px;
  background: rgba(240, 240, 240, 1);
  color: rgba(91, 91, 91, 1);
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    background: rgba(225, 225, 225, 1);
  }

  &__label {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  &__icon {
    margin-bottom: 5px;
  }

  &__text {
    font-size: 13px;
  }
}

// Highlights select modal ====================================================
.highlights-select-modal {
  color: #5b5b5b;
  font: 14px/19px sans-serif;

  &__header {
    margin-bottom: 15px;
  }

  &__album-switcher {
    margin-bottom: 15px;
  }

  &__ai-suggested-alternatives {
    margin-bottom: 30px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &--skeleton {
      height: 200px;
      align-content: flex-start;
      position: relative;
      overflow: hidden;
      mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__thumbnail {
    width: 113px;
    margin: 0 11px 11px 0;

    &--skeleton {
      @include loadingBgAnimation;
      height: 85px;
    }
  }

  &__selected-files-panel {
    position: sticky;
    bottom: -40px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .highlights-select-modal {
    &__thumbnail {
      width: calc(100% / 3 - 12px);
    }

    &__selected-files-panel {
      width: calc(100% + 40px);
      margin-left: -20px;
      bottom: -37px;

      &::after {
        border-radius: 0;
      }
    }
  }
}
</style>
